import { cloneDeep } from "lodash";
import { TWebsiteBuilderCopyableNode, TWebsiteBuilderNode } from "../nodes/websiteBuilderNode";
import { isWebsiteBuilderTable, WEBSITE_BUILDER_TABLE_HEAD_ROW_ID } from "../nodes/container/websiteBuilderTable";
import { TWebsiteBuilderSection } from "../nodes/section/websiteBuilderSection";
import {
  isWebsiteBuilderContainerWithChildren,
  TWebsiteBuilderContainer,
} from "../nodes/container/websiteBuilderContainer";
import { TWebsiteBuilderElement } from "../nodes/element/websiteBuilderElement";
import { isWebsiteBuilderHeader, WEBSITE_BUILDER_HEADER_ID } from "../nodes/section/websiteBuilderHeader";
import { isWebsiteBuilderFooter, WEBSITE_BUILDER_FOOTER_ID } from "../nodes/section/websiteBuilderFooter";
import { websiteBuilderGridCellFilled } from "../nodes/gridCell/websiteBuilderGridCell";
import { IWebsiteBuilderTab } from "../nodes/tab/websiteBuilderTab";
import { isWebsiteBuilderTabset } from "../nodes/container/websiteBuilderTabset";
import { IWebsiteBuilderRoot, WEBSITE_BUILDER_ROOT_ID } from "../nodes/root/websiteBuilderRoot";
import { isWebsiteBuilderMarquee } from "../nodes/container/websiteBuilderMarquee";

export function websiteBuilderSanitize(node: IWebsiteBuilderRoot): IWebsiteBuilderRoot;
export function websiteBuilderSanitize(node: TWebsiteBuilderSection): TWebsiteBuilderSection;
export function websiteBuilderSanitize(node: TWebsiteBuilderContainer): TWebsiteBuilderContainer;
export function websiteBuilderSanitize(node: TWebsiteBuilderElement): TWebsiteBuilderElement;
export function websiteBuilderSanitize(node: IWebsiteBuilderTab): IWebsiteBuilderTab;
export function websiteBuilderSanitize(node: TWebsiteBuilderCopyableNode): TWebsiteBuilderCopyableNode;
export function websiteBuilderSanitize(node: TWebsiteBuilderNode): TWebsiteBuilderNode {
  function traverse(node: TWebsiteBuilderNode): TWebsiteBuilderNode {
    if (node.type === "root") {
      node.id = WEBSITE_BUILDER_ROOT_ID;
      node.children.forEach(traverse);

      return node;
    } else if (node.type === "section") {
      if (isWebsiteBuilderHeader(node)) {
        node.id = WEBSITE_BUILDER_HEADER_ID;
      } else if (isWebsiteBuilderFooter(node)) {
        node.id = WEBSITE_BUILDER_FOOTER_ID;
      } else {
        node.id = crypto.randomUUID();
      }

      node.children.forEach(traverse);
      return node;
    } else if (node.type === "container") {
      node.id = crypto.randomUUID();

      if (isWebsiteBuilderTabset(node)) {
        const defaultTabIndex = node.children.findIndex(tab => tab.id === node.container.settings.defaultTabId);

        node.children.forEach(traverse);
        node.container.settings.defaultTabId = node.children[defaultTabIndex]?.id ?? node.children[0]?.id ?? null;
      } else if (isWebsiteBuilderContainerWithChildren(node)) {
        node.children.forEach(traverse);
      } else if (isWebsiteBuilderTable(node)) {
        const { head, body } = node.container.settings;

        head.row.id = WEBSITE_BUILDER_TABLE_HEAD_ROW_ID;

        head.row.cells.forEach(cell => {
          cell.id = crypto.randomUUID();
        });

        body.rows.forEach(row => {
          row.id = crypto.randomUUID();

          row.cells.forEach(cell => {
            cell.id = crypto.randomUUID();
          });
        });
      } else if (isWebsiteBuilderMarquee(node)) {
        node.container.settings.items.forEach(item => {
          item.id = crypto.randomUUID();
        });
      }

      return node;
    } else if (node.type === "grid-cell") {
      node.id = crypto.randomUUID();

      if (websiteBuilderGridCellFilled(node)) {
        traverse(node.children[0]);
      }

      return node;
    } else if (node.type === "tab") {
      node.id = crypto.randomUUID();
      node.children.forEach(traverse);

      return node;
    } else if (node.type === "element") {
      node.id = crypto.randomUUID();
      return node;
    }
  }

  return traverse(cloneDeep(node));
}